import React, { Component } from 'react';
import { IDBRole } from './types';
import { DetailsList, DetailsListLayoutMode, IColumn, mergeStyleSets, SelectionMode, Selection, IconButton, CommandButton, TextField, Panel, PrimaryButton, DefaultButton, PanelType, SearchBox, Dialog, DialogType, DialogFooter, MessageBar, MessageBarType, Spinner } from '@fluentui/react';
import { getAllRoles, addNewRole, updateRole, deleteUserRoles, deleteRoles } from './services';
import NoRecordsFound from '../../../../../core/components/NoRecordsFound';
import { IUserRoleState } from './UserRole';

interface IProps {
    dbRoles: IDBRole[] | null;
    setStateProperty: (propertyName: keyof IUserRoleState, value: any) => void;
}

interface IState {
    selectionDetails: string;
    editingCell: IDBRole | null;
    isOpenPanel: boolean;
    searchTerm: string;
    showDeleteConfirmDialog: boolean;
    deleteDialogTitle: string;
    deleteDialogMessage: string;
    pendingDeleteAction: (() => void) | null;
    roleName: string;
    roleDescription: string;
    isSaving: boolean;
    errorMessage: string;
    nameError: string;
}


class RoleTable extends Component<IProps, IState> {
    private _selection: Selection;
    private _onItemInvoked = (item: IDBRole): void => {
        console.log(`Item invoked: ${item.name}`);
    };

    constructor(props: IProps) {
        super(props);

        this._selection = new Selection({
            onSelectionChanged: () => {
                this.setState({
                    selectionDetails: this._getSelectionDetails(),
                });
            },
            getKey: this._getKey,
        });

        this.state = {
            selectionDetails: "",
            editingCell: null,
            isOpenPanel: false,
            searchTerm: '',
            showDeleteConfirmDialog: false,
            deleteDialogTitle: '',
            deleteDialogMessage: '',
            pendingDeleteAction: null,
            roleName: '',
            roleDescription: '',
            isSaving: false,
            errorMessage: '',
            nameError: ''
        };
    }

    private _getSelectionDetails(): string {
        const selectionCount = this._selection.getSelectedCount();

        switch (selectionCount) {
            case 0:
                return 'No items selected';
            case 1:
                return '1 item selected: ' + (this._selection.getSelection()[0] as IDBRole).name;
            default:
                return `${selectionCount} items selected`;
        }
    }

    private _getKey(item: any, index?: number): string {
        return item.Id?.toString() || index?.toString() || '0';
    }

    dismissPanel = () => {
        this.setState({
            isOpenPanel: false,
            editingCell: null,
            roleName: '',
            roleDescription: '',
            errorMessage: '',
            nameError: ''
        });
    };

    showPanel = () => {
        this.setState({
            isOpenPanel: true,
            roleName: '',
            roleDescription: '',
            errorMessage: '',
            nameError: ''
        });
    };

    editCell = (item: IDBRole) => {
        this.setState({
            editingCell: item,
            isOpenPanel: true,
            roleName: item.name,
            roleDescription: item.description,
            errorMessage: '',
            nameError: ''
        });
    };

    async componentDidMount() {
        const roles = await getAllRoles()
        this.props.setStateProperty && this.props.setStateProperty('dbRoles', roles || null);
    }

    private _onAddNewRole = (): void => {
        this.showPanel();
    };

    private _onRemoveSelectedRoles = (): void => {
        const selectedRoles = this._selection.getSelection() as IDBRole[];

        if (selectedRoles.length === 0) {
            return;
        }

        const roleNames = selectedRoles.map(r => r.name).join(', ');
        const title = selectedRoles.length === 1 ? 'Delete Role' : `Delete ${selectedRoles.length} Roles`;
        const message = selectedRoles.length === 1
            ? `Are you sure you want to delete the role "${roleNames}"? This action cannot be undone.`
            : `Are you sure you want to delete these ${selectedRoles.length} roles? This action cannot be undone.\n\nRoles: ${roleNames}`;

        this.setState({
            showDeleteConfirmDialog: true,
            deleteDialogTitle: title,
            deleteDialogMessage: message,
            pendingDeleteAction: () => this._executeDeleteSelectedRoles(selectedRoles)
        });
    };

    private _executeDeleteSelectedRoles = async (selectedRoles: IDBRole[]): Promise<void> => {

        const callDeleteRoles = await deleteRoles(selectedRoles.map(r => Number.parseInt(r.id)));
        if (!callDeleteRoles) {
            this.setState({ errorMessage: 'Failed to remove the role from the user. Please try again.' });
            this._closeDeleteConfirmDialog();
            return;
        }

        this.props.setStateProperty('dbRoles', this.props.dbRoles?.filter(role => !callDeleteRoles.some(selected => selected.id === role.id)) || null);

        this._closeDeleteConfirmDialog();
    };

    private _onSearchChange = (event?: React.ChangeEvent<HTMLInputElement>, newValue?: string): void => {
        this.setState({ searchTerm: newValue || '' });
    };

    private _onSearchClear = (): void => {
        this.setState({ searchTerm: '' });
    };

    private _showDeleteConfirmDialog = (title: string, message: string, onConfirm: () => void): void => {
        this.setState({
            showDeleteConfirmDialog: true,
            deleteDialogTitle: title,
            deleteDialogMessage: message,
            pendingDeleteAction: onConfirm
        });
    };

    private _closeDeleteConfirmDialog = (): void => {
        this.setState({
            showDeleteConfirmDialog: false,
            deleteDialogTitle: '',
            deleteDialogMessage: '',
            pendingDeleteAction: null
        });
    };

    private _confirmDelete = (): void => {
        const { pendingDeleteAction } = this.state;
        if (pendingDeleteAction) {
            pendingDeleteAction();
        }
    };

    private _onRoleNameChange = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string): void => {
        this.setState({
            roleName: newValue || '',
            nameError: ''
        });
    };

    private _onRoleDescriptionChange = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string): void => {
        this.setState({ roleDescription: newValue || '' });
    };

    private _validateForm = (): boolean => {
        const { roleName } = this.state;
        let isValid = true;

        if (!roleName.trim()) {
            this.setState({ nameError: 'Role name is required' });
            isValid = false;
        }

        return isValid;
    };

    private _onSave = async (): Promise<void> => {
        const { editingCell, roleName, roleDescription } = this.state;

        // Validate form
        if (!this._validateForm()) {
            return;
        }

        this.setState({ isSaving: true, errorMessage: '' });

        try {
            if (editingCell) {
                // Update existing role
                const updatedRole = await updateRole(Number.parseInt(editingCell.id), roleName, roleDescription);

                // Update the role in the list
                const { dbRoles } = this.props;
                if (dbRoles) {
                    const updatedRoles = dbRoles.map(role =>
                        role.id === editingCell.id ? updatedRole : role
                    );
                    this.props.setStateProperty('dbRoles', updatedRoles);
                }
            } else {
                // Add new role
                const newRole = await addNewRole(roleName, roleDescription);

                // Add the new role to the list
                const { dbRoles } = this.props;
                const updatedRoles = dbRoles ? [...dbRoles, newRole] : [newRole];
                this.props.setStateProperty('dbRoles', updatedRoles);
            }

            // Close panel and reset state
            this.setState({
                isSaving: false,
                isOpenPanel: false,
                editingCell: null,
                roleName: '',
                roleDescription: '',
                errorMessage: ''
            });
        } catch (error: any) {
            this.setState({
                isSaving: false,
                errorMessage: error.message || 'Failed to save role. Please try again.'
            });
        }
    };

    private _getFilteredRoles = (): IDBRole[] => {
        const { dbRoles } = this.props;
        const { searchTerm } = this.state;

        if (!dbRoles) return [];
        if (!searchTerm.trim()) return dbRoles;

        const lowerSearchTerm = searchTerm.toLowerCase();
        return dbRoles.filter(role =>
            role.name.toLowerCase().includes(lowerSearchTerm) ||
            role.description.toLowerCase().includes(lowerSearchTerm)
        );
    };

    columns: IColumn[] = [
        {
            key: 'name',
            name: 'name',
            ariaLabel: 'Column operations for name, Press to sort on name',
            iconName: 'Label',
            isIconOnly: true,
            fieldName: 'name',
            minWidth: 150,
            maxWidth: 250,
            onColumnClick: undefined,
        },
        {
            key: 'description',
            name: 'description',
            ariaLabel: 'Column operations for description, Press to sort on description',
            iconName: 'Info',
            isIconOnly: true,
            fieldName: 'description',
            minWidth: 200,
            maxWidth: 300,
            onColumnClick: undefined,
        },
        {
            key: 'action',
            name: 'Action',
            ariaLabel: 'Column operations for Action, Press to sort on Action',
            isIconOnly: true,
            minWidth: 100,
            maxWidth: 100,
            onColumnClick: undefined,
            onRender: (item, index, column) => {
                return (
                    <IconButton
                        iconProps={{ iconName: 'Edit' }}
                        title="Edit Role"
                        ariaLabel="Edit Role"
                        onClick={() => this.editCell(item)}
                    />
                );
            },
        },
    ];

    private _renderHeaderAction = (): JSX.Element => {
        return (
            <div style={{
                backgroundColor: '#f8f9fa',
                borderTop: '1px solid #edebe9',
                padding: '12px'
            }}>
                <h3 style={{ margin: '0 0 8px 0' }}>Available roles in Database</h3>
                <CommandButton
                    iconProps={{ iconName: 'Add' }}
                    text="Add"
                    onClick={this._onAddNewRole}
                    styles={{
                        root: {
                            color: '#0078d4',
                            fontWeight: 600,
                        },
                        icon: {
                            color: '#0078d4'
                        }
                    }}
                />
                <CommandButton
                    iconProps={{ iconName: 'Delete' }}
                    text="Delete selected Role(s)"
                    onClick={this._onRemoveSelectedRoles}
                    styles={{
                        root: {
                            color: '#d13438',
                            fontWeight: 600,
                        },
                        icon: {
                            color: '#d13438'
                        }
                    }}
                />
                <SearchBox
                    placeholder="Search by name or description"
                    underlined={true}
                    value={this.state.searchTerm}
                    onChange={this._onSearchChange}
                    onClear={this._onSearchClear}
                    styles={{ root: { maxWidth: '300px' } }}
                />
            </div>
        );
    };



    buttonStyles = { root: { marginRight: 8 } };
    onRenderFooterContent = () => {
        const { isSaving } = this.state;
        return (
            <div>
                <PrimaryButton
                    onClick={this._onSave}
                    styles={this.buttonStyles}
                    disabled={isSaving}
                >
                    {isSaving ? 'Saving...' : 'Save'}
                </PrimaryButton>
                <DefaultButton onClick={this.dismissPanel} disabled={isSaving}>
                    Cancel
                </DefaultButton>
            </div>
        );
    }

    render() {
        const { isOpenPanel, editingCell, showDeleteConfirmDialog, deleteDialogTitle, deleteDialogMessage, roleName, roleDescription, isSaving, errorMessage, nameError } = this.state;
        const { dbRoles } = this.props;
        const filteredRoles = this._getFilteredRoles();

        return (
            dbRoles && dbRoles.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                    <NoRecordsFound />
                </div>
            ) : (
                <div>
                    <div style={{
                        height: '600px', // Set desired height
                        overflow: 'auto',
                        border: '1px solid #edebe9'
                    }}>
                        {this._renderHeaderAction()}
                        <DetailsList
                            items={filteredRoles}
                            columns={this.columns}
                            selectionMode={SelectionMode.multiple}
                            setKey="multiple"
                            layoutMode={DetailsListLayoutMode.justified}
                            isHeaderVisible={true}
                            selection={this._selection}
                            selectionPreservedOnEmptyClick={true}
                            onItemInvoked={this._onItemInvoked}
                            enterModalSelectionOnTouch={true}
                            ariaLabelForSelectionColumn="Toggle selection"
                            ariaLabelForSelectAllCheckbox="Toggle selection for all items"
                            checkButtonAriaLabel="select row"
                        />
                        <Panel
                            isOpen={isOpenPanel}
                            onDismiss={this.dismissPanel}
                            headerText={editingCell ? `Edit Role: ${editingCell.name}` : "Add New Role"}
                            closeButtonAriaLabel="Close"
                            onRenderFooterContent={this.onRenderFooterContent}
                            isFooterAtBottom={true}
                            customWidth='400px'
                            type={PanelType.customNear}
                            isLightDismiss
                        >
                            {isSaving && (
                                <Spinner label="Saving role..." ariaLive="assertive" labelPosition="right" />
                            )}

                            {errorMessage && (
                                <MessageBar
                                    messageBarType={MessageBarType.error}
                                    isMultiline={false}
                                    onDismiss={() => this.setState({ errorMessage: '' })}
                                    dismissButtonAriaLabel="Close"
                                    styles={{ root: { marginBottom: 15 } }}
                                >
                                    {errorMessage}
                                </MessageBar>
                            )}

                            <TextField
                                label="Role Name"
                                required
                                borderless
                                value={roleName}
                                onChange={this._onRoleNameChange}
                                errorMessage={nameError}
                                disabled={isSaving}
                                styles={{ root: { marginBottom: 15 } }}
                            />
                            <TextField
                                label='Description'
                                borderless
                                multiline
                                rows={4}
                                value={roleDescription}
                                onChange={this._onRoleDescriptionChange}
                                disabled={isSaving}
                                styles={{
                                    root: { marginBottom: 15 },
                                    field: {
                                        minHeight: '80px',
                                        resize: 'vertical'
                                    }
                                }}
                            />
                        </Panel>
                    </div>

                    {/* Delete Confirmation Dialog */}
                    <Dialog
                        hidden={!showDeleteConfirmDialog}
                        onDismiss={this._closeDeleteConfirmDialog}
                        dialogContentProps={{
                            type: DialogType.normal,
                            title: deleteDialogTitle,
                            subText: deleteDialogMessage,
                        }}
                        modalProps={{
                            isBlocking: true,
                            styles: { main: { maxWidth: 450 } },
                        }}
                    >
                        <DialogFooter>
                            <PrimaryButton
                                onClick={this._confirmDelete}
                                text="Delete"
                                styles={{
                                    root: { backgroundColor: '#d13438' },
                                    rootHovered: { backgroundColor: '#a4262c' },
                                    rootPressed: { backgroundColor: '#8a1c1f' }
                                }}
                            />
                            <DefaultButton onClick={this._closeDeleteConfirmDialog} text="Cancel" />
                        </DialogFooter>
                    </Dialog>
                </div>
            )
        );
    }
}

export default RoleTable;